import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { api } from '../../api/client';
import type { DeinterlaceMode } from '../../api/types';
import { apiErrorText } from '../../api/errorText';
import { useApi } from '../../api/useApi';
import AsyncSection from '../../components/AsyncSection';
import HardwareDeviceCard from '../../components/settings/HardwareDeviceCard';
import ManagedSetting from './ManagedSetting';

/// What the server does when a client cannot play a file as it is, and what the hardware can
/// actually do about it. The two belong together: a pool size means nothing without knowing
/// whether there is an encoder at all.
export default function TranscodingSettings() {
  const { t } = useTranslation();
  const view = useApi(['settings', 'transcoding'], api.transcodingSettings);

  // The draft wins while it exists; otherwise the field shows what the server reports.
  const [draftPoolSize, setDraftPoolSize] = useState<string>();
  const [draftMaxBitrate, setDraftMaxBitrate] = useState<string>();
  const [draftDeinterlace, setDraftDeinterlace] = useState<string>();
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string>();
  const [draftLiveTvQuality, setDraftLiveTvQuality] = useState<string | undefined>(undefined);
  const [busy, setBusy] = useState(false);
  const [switching, setSwitching] = useState(false);

  const poolSize = draftPoolSize ?? view.data?.settings.transcodeWorkerPoolSize.value.toString() ?? '';
  const maxBitrate = draftMaxBitrate ?? view.data?.settings.maxStreamingBitrate.value?.toString() ?? '';
  const deinterlace = draftDeinterlace ?? view.data?.settings.deinterlaceMode.value ?? 'FrameRate';
  const liveTvQuality =
    draftLiveTvQuality ?? (view.data?.settings.allowLiveTvQualitySelection.value ? 'true' : 'false');

  const save = async () => {
    setBusy(true);
    setSaved(false);
    setError(undefined);

    try {
      const bitrate = maxBitrate.trim();

      await api.saveSettings({
        transcodeWorkerPoolSize: Number(poolSize) || 0,
        maxStreamingBitrate: bitrate ? Number(bitrate) : undefined,
        clearMaxStreamingBitrate: bitrate === '',
        deinterlaceMode: deinterlace as DeinterlaceMode,
        allowLiveTvQualitySelection: liveTvQuality === 'true',
      });

      setDraftPoolSize(undefined);
      setDraftMaxBitrate(undefined);
      setDraftDeinterlace(undefined);
      setDraftLiveTvQuality(undefined);
      view.reload();
      setSaved(true);
    } catch (caught) {
      setError(apiErrorText(caught, t).message);
    } finally {
      setBusy(false);
    }
  };

  /// Switching a device on or off takes effect at once: there is no draft to save, and a card
  /// that looked switched off until somebody pressed Save would be a lie about the server.
  const setDeviceEnabled = async (deviceId: string, enabled: boolean) => {
    const current = view.data?.settings.disabledHardwareDevices.value ?? [];

    const next = enabled
      ? current.filter((id) => id !== deviceId)
      : [...new Set([...current, deviceId])];

    setSwitching(true);
    setError(undefined);

    try {
      await api.saveSettings({ disabledHardwareDevices: next });
      view.reload();
    } catch (caught) {
      setError(apiErrorText(caught, t).message);
    } finally {
      setSwitching(false);
    }
  };

  const hardware = view.data?.hardware;
  const hostKind = view.data?.runtime.hostKind;
  const devices = hardware?.devices ?? [];
  const allSwitchedOff = devices.length > 0 && devices.every((device) => !device.isEnabled);

  return (
    <AsyncSection loading={view.loading} error={view.error} onRetry={view.reload}>
      <Stack spacing={3}>
        <Card variant="outlined">
          <CardContent>
            <Stack spacing={3}>
              {error && <Alert severity="error">{error}</Alert>}
              {saved && <Alert severity="success">{t('settings.saved')}</Alert>}

              <ManagedSetting
                setting={view.data?.settings.transcodeWorkerPoolSize}
                label={t('settings.transcoding.poolSize')}
                hint={t('settings.transcoding.poolSizeHint')}
                value={poolSize}
                onChange={setDraftPoolSize}
                type="number"
              />

              <ManagedSetting
                setting={view.data?.settings.maxStreamingBitrate}
                label={t('settings.transcoding.maxBitrate')}
                hint={t('settings.transcoding.maxBitrateHint')}
                value={maxBitrate}
                onChange={setDraftMaxBitrate}
                type="number"
              />

              <ManagedSetting
                setting={view.data?.settings.deinterlaceMode}
                label={t('settings.transcoding.deinterlace')}
                hint={t('settings.transcoding.deinterlaceHint')}
                value={deinterlace}
                onChange={setDraftDeinterlace}
                choices={[
                  { value: 'FrameRate', label: t('settings.transcoding.deinterlaceFrameRate') },
                  { value: 'FieldRate', label: t('settings.transcoding.deinterlaceFieldRate') },
                ]}
              />

              <ManagedSetting
                setting={view.data?.settings.allowLiveTvQualitySelection}
                label={t('settings.transcoding.liveTvQuality')}
                hint={t('settings.transcoding.liveTvQualityHint')}
                value={liveTvQuality}
                onChange={setDraftLiveTvQuality}
                choices={[
                  { value: 'false', label: t('settings.transcoding.liveTvQualityOff') },
                  { value: 'true', label: t('settings.transcoding.liveTvQualityOn') },
                ]}
              />

              <Box>
                <Button variant="contained" onClick={() => void save()} disabled={busy}>
                  {t('common.save')}
                </Button>
              </Box>
            </Stack>
          </CardContent>
        </Card>

        <Stack
          direction="row"
          spacing={2}
          useFlexGap
          sx={{ alignItems: 'center', flexWrap: 'wrap', rowGap: 1 }}
        >
          <Box sx={{ flexGrow: 1, minWidth: 200 }}>
            <Typography variant="h3">{t('settings.transcoding.hardware')}</Typography>
            <Typography variant="body2" color="text.secondary">
              {t('hardware.hint')}
            </Typography>
          </Box>
          <Chip
            size="small"
            variant="outlined"
            label={t(`runtime.${hostKind ?? 'BareMetal'}`)}
          />
          <Button
            onClick={() => {
              void api.refreshHardware().then(view.reload);
            }}
          >
            {t('common.refresh')}
          </Button>
        </Stack>

        {hardware?.error ? (
          <Alert severity="error">
            <AlertTitle>{t('dashboard.probeFailed')}</AlertTitle>
            <div>{hardware.error}</div>
            {hardware.remedy && <div>{hardware.remedy}</div>}
          </Alert>
        ) : devices.length > 0 ? (
          <Stack spacing={2}>
            {allSwitchedOff && (
              <Alert severity="warning">
                <AlertTitle>{t('hardware.allSwitchedOff')}</AlertTitle>
                {t('hardware.allSwitchedOffHint')}
              </Alert>
            )}

            {devices.map((device) => (
              <HardwareDeviceCard
                key={device.id}
                device={device}
                busy={switching}
                onToggle={(enabled) => void setDeviceEnabled(device.id, enabled)}
              />
            ))}
          </Stack>
        ) : (
          <Alert severity="warning">
            <AlertTitle>{t('dashboard.hardwareEmpty')}</AlertTitle>
            {t('dashboard.hardwareEmptyHint', { context: hostKind })}
          </Alert>
        )}
      </Stack>
    </AsyncSection>
  );
}
